import { useState } from 'react';
import {
  apiErrorText,
  useListAccumulationEntriesQuery,
  useSetAccumulationEntriesMarkMutation,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Label } from '@shared/ui/Input';
import { fmtNum } from '@shared/lib/smetaLabels';
import { PAGE_SIZE } from './accumulationLabels';
import {
  CheckLabel,
  ErrMargin,
  FieldZero,
  Filters,
  InlineBadge,
  Muted,
  MutedMargin,
  NarrowInput,
  Pager,
  Table,
  TableTitle,
  ThCheckbox,
} from './styled';

interface EntriesPanelProps {
  ledgerId: string;
  canWrite: boolean;
}

/**
 * Лента «данные ПА» с ручной простановкой марки РД.
 *
 * Уточнение сметно-ценового отдела 03.09.2026: «Касаемо марки у старых строк:
 * оставляем как есть, мы проставим вручную». На проде без марки 9 044 строки
 * из 13 175 исторических, поэтому кроме правки одной строки нужна пакетная
 * простановка по фильтру — поштучно это недели работы.
 *
 * Лента персистентная (21,6 тыс. строк), выборка обязательно страничная.
 */
export function EntriesPanel({ ledgerId, canWrite }: EntriesPanelProps) {
  const [page, setPage] = useState(0);
  const [source, setSource] = useState('');
  const [titul, setTitul] = useState('');
  const [paNo, setPaNo] = useState('');
  const [search, setSearch] = useState('');
  const [missingMark, setMissingMark] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [markValue, setMarkValue] = useState('');
  const [markError, setMarkError] = useState<string | null>(null);

  const { data, isFetching } = useListAccumulationEntriesQuery({
    id: ledgerId,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    source: source || undefined,
    titul: titul.trim() || undefined,
    pa_no: paNo.trim() || undefined,
    search: search.trim() || undefined,
    missing_mark: missingMark || undefined,
  });
  const [setMark, { isLoading: saving }] = useSetAccumulationEntriesMarkMutation();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // Смена фильтра меняет состав страницы — выбранные id к ней уже не относятся.
  const resetPage = () => {
    setPage(0);
    setSelected([]);
  };

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const pageIds = items.map((e) => e.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  const applyMark = async (value: string | null) => {
    setMarkError(null);
    try {
      await setMark({ id: ledgerId, entry_ids: selected, mark_rd: value }).unwrap();
      setSelected([]);
      setMarkValue('');
    } catch (e) {
      setMarkError(apiErrorText(e, 'Не удалось сохранить марку'));
    }
  };

  return (
    <>
      <TableTitle>
        Лента «данные ПА» <Badge $tone="neutral">{total}</Badge>
      </TableTitle>
      <Filters>
        <FieldZero>
          <Label>Титул</Label>
          <NarrowInput
            placeholder="6013"
            value={titul}
            onChange={(e) => {
              setTitul(e.target.value);
              resetPage();
            }}
          />
        </FieldZero>
        <FieldZero>
          <Label>№ ПА</Label>
          <NarrowInput
            placeholder="6013-АК-СКМ-8509"
            value={paNo}
            onChange={(e) => {
              setPaNo(e.target.value);
              resetPage();
            }}
          />
        </FieldZero>
        <FieldZero>
          <Label>Поиск по шифру/наименованию</Label>
          <NarrowInput
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
          />
        </FieldZero>
        <CheckLabel>
          <input
            type="checkbox"
            checked={source === 'seed'}
            onChange={(e) => {
              setSource(e.target.checked ? 'seed' : '');
              resetPage();
            }}
          />
          только исторические
        </CheckLabel>
        <CheckLabel>
          <input
            type="checkbox"
            checked={missingMark}
            onChange={(e) => {
              setMissingMark(e.target.checked);
              resetPage();
            }}
          />
          только без марки РД
        </CheckLabel>
      </Filters>

      {canWrite && (
        <Filters>
          <FieldZero>
            <Label>Марка РД для выбранных ({selected.length})</Label>
            <NarrowInput
              placeholder="КЖ6.2"
              value={markValue}
              onChange={(e) => setMarkValue(e.target.value)}
            />
          </FieldZero>
          <Button
            type="button"
            $variant="primary"
            $size="sm"
            disabled={saving || selected.length === 0 || !markValue.trim()}
            onClick={() => applyMark(markValue.trim())}
          >
            {saving ? 'Сохраняю…' : 'Проставить'}
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={saving || selected.length === 0}
            onClick={() => applyMark(null)}
            title="Снять ручную марку у выбранных строк"
          >
            Снять марку
          </Button>
        </Filters>
      )}
      {markError && <ErrMargin>{markError}</ErrMargin>}

      {items.length === 0 && !isFetching && (
        <MutedMargin>Под фильтр не попало ни одной строки.</MutedMargin>
      )}
      {items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <ThCheckbox>
                <input
                  type="checkbox"
                  aria-label="Выбрать всё на странице"
                  checked={allOnPageSelected}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked
                        ? Array.from(new Set([...prev, ...pageIds]))
                        : prev.filter((id) => !pageIds.includes(id)),
                    )
                  }
                />
              </ThCheckbox>
              <th>Титул</th>
              <th>Марка РД</th>
              <th>№ ПА</th>
              <th>Шифр ЕР</th>
              <th>Наименование</th>
              <th>Ед.</th>
              <th className="num">Объём</th>
              <th>Примечание</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label="Выбрать строку"
                    checked={selected.includes(e.id)}
                    disabled={!canWrite}
                    onChange={() => toggle(e.id)}
                  />
                </td>
                <td>{e.titul || '—'}</td>
                <td>
                  {e.mark_rd || <Muted>нет</Muted>}
                  {e.mark_rd_manual && (
                    <InlineBadge $tone="info">
                      вручную
                    </InlineBadge>
                  )}
                </td>
                <td>{e.pa_no || '—'}</td>
                <td>{e.code || '—'}</td>
                <td>{e.name || '—'}</td>
                <td>{e.unit || '—'}</td>
                <td className="num">{e.quantity == null ? '—' : fmtNum(e.quantity)}</td>
                <td>{e.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Pager>
        <Button
          type="button"
          $variant="ghost"
          $size="sm"
          disabled={page === 0 || isFetching}
          onClick={() => {
            setPage((p) => Math.max(0, p - 1));
            setSelected([]);
          }}
        >
          Назад
        </Button>
        <span>
          {total === 0
            ? '0'
            : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} из ${total}`}
        </span>
        <Button
          type="button"
          $variant="ghost"
          $size="sm"
          disabled={(page + 1) * PAGE_SIZE >= total || isFetching}
          onClick={() => {
            setPage((p) => p + 1);
            setSelected([]);
          }}
        >
          Вперёд
        </Button>
      </Pager>
    </>
  );
}
